import { c as _c } from "react/compiler-runtime";
import { Button } from "@osmix/ui/button";
import { Bell, ChevronDown, FileText, FileCheck, FolderTree, Globe, Kanban, LayoutDashboard, LayoutTemplate, LifeBuoy, Lightbulb, Lock, LogOut, Mail, Megaphone, Menu, MessageSquare, MessageSquareWarning, Package, Percent, RotateCcw, ScrollText, ShieldCheck, ShoppingBag, TicketPercent, CalendarRange, Inbox, ShoppingCart, Store, Trophy, Truck, UserCheck, Users, Wallet, WandSparkles } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useNotificationCount } from "@/api/hooks/use-notifications";
import { HelpButton } from "@/components/onboarding/help-button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { navWithLocks } from "@/lib/admin-permissions";
import { activeGroupKey, expandActive, readExpandedGroups, toggleGroup, writeExpandedGroups } from "@/lib/nav-groups";
import { cn } from "@/lib/utils";
import { useAuth } from "@/store";
type NavEntry = {
  to: string;
  key: string;
  icon: ComponentType<{
    className?: string;
  }>;
  permission: string;
};
type NavGroup = {
  key: string;
  items: NavEntry[];
};

// Sidebar nav — grew past what a heading navbar holds once Orders, Carts and
// Delivery settings landed, so the admin moved to a left rail: a fixed vertical
// rail on desktop, a burger-triggered drawer on mobile.
//
// Entries are clustered by what the operator is doing, not by which service
// serves them, so a role with a narrow permission set reads as a short list of
// labelled areas rather than a flat column. Groups whose every item is
// permission-filtered away disappear entirely (see NavItems), which is what
// keeps the founder/investor workspaces from showing empty headings.
const NAV_GROUPS: NavGroup[] = [{
  key: "navGroups.overview",
  items: [{
    to: "/dashboard",
    key: "nav.dashboard",
    icon: LayoutDashboard,
    permission: "page.dashboard"
  }, {
    to: "/reports",
    key: "nav.reports",
    icon: FileText,
    permission: "page.reports"
  }, {
    to: "/finances",
    key: "nav.finances",
    icon: Wallet,
    permission: "page.finances"
  }, {
    to: "/commissions",
    key: "nav.commissions",
    icon: Percent,
    permission: "page.finances"
  }]
}, {
  key: "navGroups.catalog",
  items: [{
    to: "/products",
    key: "nav.products",
    icon: Package,
    permission: "page.products"
  },
  // Модерация отзывов: жалобы продавцов на отзывы (fd-admin-05).
  {
    to: "/review-complaints",
    key: "nav.reviewComplaints",
    icon: MessageSquareWarning,
    permission: "page.products"
  },
  // ИИ-загрузка каталога: материалы бизнеса → магазин и товары (04.10.2026).
  {
    to: "/catalog-import",
    key: "nav.catalogImport",
    icon: WandSparkles,
    permission: "page.catalog_import"
  }, {
    to: "/categories",
    key: "nav.categories",
    icon: FolderTree,
    permission: "page.categories"
  },
  // Документы о соответствии: очередь проверки, отчёт, журнал (31.10.2026).
  {
    to: "/compliance",
    key: "nav.compliance",
    icon: FileCheck,
    permission: "page.products"
  }, {
    to: "/feed",
    key: "nav.feed",
    icon: LayoutTemplate,
    permission: "page.inspiration_feed"
  }, {
    to: "/ideas",
    key: "nav.ideas",
    icon: Lightbulb,
    permission: "page.ideas"
  }]
}, {
  key: "navGroups.commerce",
  items: [{
    to: "/orders",
    key: "nav.orders",
    icon: ShoppingCart,
    permission: "page.orders"
  }, {
    to: "/returns",
    key: "nav.returns",
    icon: RotateCcw,
    permission: "page.orders"
  }, {
    to: "/carts",
    key: "nav.carts",
    icon: ShoppingBag,
    permission: "page.carts"
  }, {
    to: "/promos",
    key: "nav.promos",
    icon: TicketPercent,
    permission: "page.promos"
  },
  // Клиенты продавцов: привязка покупателя к магазину и льготный срок (17.10.2026).
  {
    to: "/seller-clients",
    key: "nav.sellerClients",
    icon: UserCheck,
    permission: "page.promos"
  },
  // Акции площадки и очередь заявок продавцов на участие (П3).
  {
    to: "/platform-promo-campaigns",
    key: "nav.platformPromoCampaigns",
    icon: CalendarRange,
    permission: "page.promos"
  }, {
    to: "/promo-applications",
    key: "nav.promoApplications",
    icon: Inbox,
    permission: "page.promos"
  },
  // Реклама CPA/CPM (этап 8, П1): модерация кампаний, отчёт, атрибуции.
  {
    to: "/ad-campaigns",
    key: "nav.adCampaigns",
    icon: Megaphone,
    permission: "page.ads"
  }]
}, {
  key: "navGroups.people",
  items: [{
    to: "/applications",
    key: "nav.applications",
    icon: Kanban,
    permission: "page.applications"
  }, {
    to: "/shops",
    key: "nav.shops",
    icon: Store,
    permission: "page.shops"
  }, {
    to: "/users",
    key: "nav.users",
    icon: Users,
    permission: "page.users"
  }, {
    to: "/top-buyers",
    key: "nav.topBuyers",
    icon: Trophy,
    permission: "page.users"
  }]
}, {
  key: "navGroups.engagement",
  items: [{
    to: "/notification-campaigns",
    key: "nav.notificationCampaigns",
    icon: Megaphone,
    permission: "page.notification_campaigns"
  },
  // «Уведомления → Почта»: статус провайдера, журнал писем, шаблоны.
  {
    to: "/email",
    key: "nav.email",
    icon: Mail,
    permission: "page.integrations"
  }, {
    to: "/support",
    key: "nav.support",
    icon: LifeBuoy,
    permission: "page.support"
  },
  // Журнал действий — рядом с ролями и интеграциями: это раздел про
  // администрирование самой консоли, а не про товары и заказы.
  {
    to: "/audit",
    key: "nav.audit",
    icon: ScrollText,
    permission: "action.audit.view"
  }]
}, {
  key: "navGroups.settings",
  items: [{
    to: "/delivery",
    key: "nav.delivery",
    icon: Truck,
    permission: "page.delivery"
  }, {
    to: "/geo",
    key: "nav.geo",
    icon: Globe,
    permission: "page.geo"
  }, {
    to: "/integrations",
    key: "nav.integrations",
    icon: MessageSquare,
    permission: "page.integrations"
  }, {
    to: "/platform-roles",
    key: "nav.platformRoles",
    icon: ShieldCheck,
    permission: "page.platform_roles"
  }]
}];
const navItemClass = (active: boolean) => cn("flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors", active ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground");
function NavItems(t0) {
  const $ = _c(3);
  const {
    onNavigate
  } = t0;
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp);
  const groups = navWithLocks(NAV_GROUPS, user);
  const showLabels = groups.length > 1;
  const {
    pathname
  } = useLocation();
  const active = activeGroupKey(groups.map(_temp3), pathname);
  const [expanded, setExpanded] = useState(readExpandedGroups);
  useEffect(() => {
    setExpanded(prev => {
      const next = expandActive(prev, active);
      if (next !== prev) {
        writeExpandedGroups(next);
      }
      return next;
    });
  }, [active]);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = key => setExpanded(prev_0 => {
      const next_0 = toggleGroup(prev_0, key);
      writeExpandedGroups(next_0);
      return next_0;
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const onToggle = t1;
  const t2 = "flex flex-col gap-3";
  const t4 = groups.map(group_0 => {
    const open = !showLabels || expanded.includes(group_0.key);
    const listId = `nav-group-${group_0.key.replace(/\W+/g, "-")}`;
    return <div key={group_0.key} className="flex flex-col gap-1" role="group" aria-label={t(group_0.key)}>{showLabels && <button type="button" onClick={() => onToggle(group_0.key)} aria-expanded={open} aria-controls={listId} className={cn("flex items-center justify-between gap-2 rounded-md px-3 py-1 text-left text-[11px] font-semibold uppercase tracking-wider transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", group_0.key === active ? "text-foreground/80" : "text-muted-foreground/70")}><span className="truncate">{t(group_0.key)}</span><ChevronDown className={cn("size-3.5 shrink-0 transition-transform duration-200", !open && "-rotate-90")} aria-hidden="true" /></button>}{open && <div id={listId} className="flex flex-col gap-1">{group_0.items.map(t3 => {
          const {
            to,
            key: key_0,
            icon: Icon,
            locked
          } = t3;
          return locked ? <span key={to} aria-disabled="true" title={t("nav.locked")} className={cn(navItemClass(false), "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-muted-foreground")}><Icon className="size-4 shrink-0" /><span className="truncate">{t(key_0)}</span><Lock className="ml-auto size-3.5 shrink-0" aria-label={t("nav.locked")} /></span> : <NavLink key={to} to={to} onClick={onNavigate} className={_temp4}><Icon className="size-4 shrink-0" />{t(key_0)}</NavLink>;
        })}</div>}</div>;
  });
  let t5;
  if ($[1] !== t4) {
    t5 = <nav className={t2}>{t4}</nav>;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  return t5;
}
function _temp4(t0) {
  const {
    isActive
  } = t0;
  return navItemClass(isActive);
}
function _temp3(group) {
  return {
    ...group,
    items: group.items.filter(_temp2)
  };
}
function _temp2(entry) {
  return !entry.locked;
}
function _temp(s) {
  return s.user;
}
function PersonalNotificationsLink(t0) {
  const $ = _c(15);
  const {
    onNavigate
  } = t0;
  const {
    t
  } = useTranslation();
  const count = useNotificationCount().data?.count ?? 0;
  let t1;
  if ($[0] !== count || $[1] !== t) {
    t1 = t("notifications.bellLabel", {
      count
    });
    $[0] = count;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Bell className="size-4 shrink-0" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("nav.notifications");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <span>{t3}</span>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== count) {
    t5 = count > 0 && <span className="ml-auto rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground" aria-live="polite">{count > 99 ? "99+" : count}</span>;
    $[8] = count;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onNavigate || $[11] !== t1 || $[12] !== t4 || $[13] !== t5) {
    t6 = <NavLink to="/notifications" onClick={onNavigate} className={_temp5} aria-label={t1}>{t2}{t4}{t5}</NavLink>;
    $[10] = onNavigate;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp5(t0) {
  const {
    isActive
  } = t0;
  return navItemClass(isActive);
}
function AccountMenu() {
  const $ = _c(22);
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth(_temp6);
  const signOut = useAuth(_temp7);
  let t0;
  if ($[0] !== navigate || $[1] !== signOut) {
    t0 = async () => {
      await signOut();
      navigate("/login", {
        replace: true
      });
    };
    $[0] = navigate;
    $[1] = signOut;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const onSignOut = t0;
  const t1 = user?.name;
  let t2;
  if ($[3] !== t1) {
    t2 = <p className="truncate text-sm font-medium">{t1}</p>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = user?.email;
  let t4;
  if ($[5] !== t3) {
    t4 = <p className="truncate text-xs text-muted-foreground">{t3}</p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div className="min-w-0">{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("auth.signOut");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("auth.signOut");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <LogOut className="size-4" />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== onSignOut || $[16] !== t6 || $[17] !== t7) {
    t9 = <Button variant="ghost" size="icon-sm" onClick={onSignOut} title={t6} aria-label={t7}>{t8}</Button>;
    $[15] = onSignOut;
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <div className="flex items-center justify-between gap-2 px-3">{t5}{t9}</div>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}

// The rail's contents, shared verbatim between the desktop aside and the
// mobile drawer. `onNavigate` lets the drawer close itself when a link is hit.
function _temp7(s_0) {
  return s_0.signOut;
}
function _temp6(s) {
  return s.user;
}
function RailContents(t0) {
  const $ = _c(10);
  const {
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== onNavigate) {
    t1 = <NavItems onNavigate={onNavigate} />;
    $[0] = onNavigate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate) {
    t2 = <PersonalNotificationsLink onNavigate={onNavigate} />;
    $[2] = onNavigate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <AccountMenu />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className="mt-auto flex flex-col gap-3 border-t pt-4">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[7] = t1;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function AdminLayout(t0) {
  const $ = _c(43);
  const {
    children
  } = t0;
  const {
    t
  } = useTranslation();
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== t) {
    t1 = t("app.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="text-lg font-bold">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <HelpButton />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className="flex items-center justify-between gap-2 px-3 pb-4">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <RailContents />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col overflow-y-auto border-r p-4 md:flex">{t4}{t5}</aside>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("nav.menu");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Menu className="size-4" />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7) {
    t9 = <SheetTrigger asChild={true}><Button variant="outline" size="icon-sm" aria-label={t7}>{t8}</Button></SheetTrigger>;
    $[13] = t7;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("app.title");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <SheetHeader className="p-0"><SheetTitle className="text-lg font-bold">{t10}</SheetTitle></SheetHeader>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <RailContents onNavigate={() => setOpen(false)} />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11) {
    t13 = <SheetContent side="left" className="w-64 overflow-y-auto p-4">{t11}{t12}</SheetContent>;
    $[20] = t11;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== open || $[23] !== t13 || $[24] !== t9) {
    t14 = <Sheet open={open} onOpenChange={setOpen}>{t9}{t13}</Sheet>;
    $[22] = open;
    $[23] = t13;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t) {
    t15 = t("app.title");
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <span className="font-bold">{t15}</span>;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <HelpButton className="ml-auto" />;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t14 || $[32] !== t16) {
    t18 = <header className="flex items-center gap-2 border-b p-3 md:hidden">{t14}{t16}{t17}</header>;
    $[31] = t14;
    $[32] = t16;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== children) {
    t19 = <main className="min-w-0 flex-1 p-4 md:p-6">{children}</main>;
    $[34] = children;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <LegalFooter />;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t18 || $[38] !== t19) {
    t21 = <div className="flex min-w-0 flex-1 flex-col">{t18}{t19}{t20}</div>;
    $[37] = t18;
    $[38] = t19;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t21 || $[41] !== t6) {
    t22 = <div className="flex min-h-screen bg-background">{t6}{t21}</div>;
    $[40] = t21;
    $[41] = t6;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  return t22;
}

// Правовая информация оператора (ИП Алавердян М. А., бренд OSMIX). Документы —
// PDF, отдаются веб-сервером продавца; открываются в новой вкладке.
function LegalFooter() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [["\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u041F\u0414\u043D", "https://seller.osmix.ru/docs/b2c/\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 OSMIX.pdf"], ["\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u0438", "https://seller.osmix.ru/docs/b2c/\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u0438 OSMIX.pdf"], ["\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u0441\u043A\u043E\u0435 \u0441\u043E\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435", "https://seller.osmix.ru/docs/b2c/\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u0441\u043A\u043E\u0435 \u0441\u043E\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435 OSMIX.pdf"], ["\u041E\u0444\u0435\u0440\u0442\u0430 \u0434\u043B\u044F \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432", "https://seller.osmix.ru/docs/b2b/\u0414\u043E\u0433\u043E\u0432\u043E\u0440-\u043E\u0444\u0435\u0440\u0442\u0430 \u0434\u043B\u044F \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432 OSMIX.pdf"], ["\u041F\u0414\u043D \u043F\u0440\u0435\u0434\u0441\u0442\u0430\u0432\u0438\u0442\u0435\u043B\u0435\u0439 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432", "https://seller.osmix.ru/docs/b2b/\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0438 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 \u043F\u0440\u0435\u0434\u0441\u0442\u0430\u0432\u0438\u0442\u0435\u043B\u0435\u0439 \u043F\u0440\u043E\u0434\u0430\u0432\u0446\u043E\u0432.pdf"]];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const links = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <footer className="border-t px-4 py-3 md:px-6"><div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"><span>OSMIX · ИП Алавердян М. А. · правовая информация:</span>{links.map(_temp8)}</div></footer>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp8(t0) {
  const [label, href] = t0;
  return <a key={label} href={encodeURI(href)} target="_blank" rel="noreferrer" className="hover:text-foreground hover:underline">{label}</a>;
}